// src/ConveFilePanel.tsx

import React, { useEffect, useState } from 'react';
import { ConveFile } from './types/conve';
import conveAxios from './api/conveApi';
import './ConveFilePanel.css';

// @ts-ignore
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
// @ts-ignore
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
// @ts-ignore
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
// @ts-ignore
import * as XLSX from 'xlsx';

const LANG_EXT: Record<string, string> = {
  javascript: 'js', typescript: 'ts', jsx: 'jsx', tsx: 'tsx',
  java: 'java', python: 'py', json: 'json', xml: 'xml',
  html: 'html', css: 'css', markdown: 'md', sql: 'sql',
  bash: 'sh', shell: 'sh', yaml: 'yaml', yml: 'yaml',
  kotlin: 'kt', swift: 'swift', go: 'go', rust: 'rs',
  cpp: 'cpp', c: 'c', csharp: 'cs', ruby: 'rb', php: 'php',
  text: 'txt',
};

const EXT_LANG: Record<string, string> = {
  js: 'javascript', ts: 'typescript', jsx: 'jsx', tsx: 'tsx',
  java: 'java', py: 'python', json: 'json', xml: 'xml',
  html: 'html', css: 'css', md: 'markdown', txt: 'text',
  sql: 'sql', sh: 'bash', yaml: 'yaml', yml: 'yaml',
};

function getLanguage(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return EXT_LANG[ext] || 'text';
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function colLabel(n: number): string {
  let label = '';
  n += 1;
  while (n > 0) {
    n--;
    label = String.fromCharCode(65 + (n % 26)) + label;
    n = Math.floor(n / 26);
  }
  return label;
}

export function getFileIcon(mimeType: string, name: string): string {
  const ext = name?.split('.').pop()?.toLowerCase() || '';
  if (mimeType?.startsWith('image/') || ['png','jpg','jpeg','gif','webp','bmp','svg'].includes(ext)) return '🖼️';
  if (mimeType?.includes('pdf') || ext === 'pdf') return '📕';
  if (mimeType?.includes('wordprocessingml') || ['doc','docx'].includes(ext)) return '📘';
  if (mimeType?.includes('spreadsheetml') || mimeType?.includes('ms-excel') || ['xls','xlsx'].includes(ext)) return '📗';
  if (mimeType?.includes('presentationml') || mimeType?.includes('ms-powerpoint') || ['ppt','pptx'].includes(ext)) return '📙';
  if (mimeType?.includes('hwp') || ext === 'hwp') return '📄';
  if (['js','ts','jsx','tsx','java','py','kt','go','rs','cpp','c','cs','rb','php','swift'].includes(ext)) return '💻';
  if (['html','css','scss','sass','less','xml'].includes(ext)) return '🌐';
  if (['json','yaml','yml'].includes(ext)) return '⚙️';
  if (['sql'].includes(ext)) return '🗄️';
  if (['sh','bash'].includes(ext)) return '🖥️';
  if (['md'].includes(ext)) return '📝';
  if (['csv'].includes(ext)) return '📊';
  if (mimeType?.startsWith('text/') || ['txt'].includes(ext)) return '📄';
  return '📎';
}

export interface CodePanelItem {
  type: 'code';
  language: string;
  code: string;
}

export type PanelItem = ConveFile | CodePanelItem;

function isCodeItem(item: PanelItem): item is CodePanelItem {
  return (item as CodePanelItem).type === 'code';
}

type FileContentType = 'image' | 'pdf' | 'excel' | 'text';
interface FileContentResponse {
  type: FileContentType;
  mimeType: string;
  fileName: string;
  data: string;
}

interface ExcelSheet {
  name: string;
  data: string[][];
  colCount: number;
}

interface Props {
  item: PanelItem | null;
  roomId: number;
  onClose: () => void;
  theme?: 'dark' | 'light';
  width?: number;
}

const ConveFilePanel: React.FC<Props> = ({ item, roomId, onClose, theme = 'dark', width }) => {
  const [fileContent, setFileContent]   = useState('');
  const [imageSrc, setImageSrc]         = useState('');
  const [pdfSrc, setPdfSrc]             = useState('');
  const [excelSheets, setExcelSheets]   = useState<ExcelSheet[]>([]);
  const [activeSheet, setActiveSheet]   = useState(0);
  const [contentType, setContentType]   = useState<FileContentType>('text');
  const [loading, setLoading]           = useState(false);
  const [copied, setCopied]             = useState(false);
  const [saveFilename, setSaveFilename] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);

  const isOpen = !!item;
  const isCode = item ? isCodeItem(item) : false;
  const isFile = item && !isCode;

  useEffect(() => {
    if (!item || isCode) {
      setFileContent(''); setImageSrc(''); setPdfSrc(''); setExcelSheets([]);
      return;
    }

    const file = item as ConveFile;
    setLoading(true);
    setFileContent(''); setImageSrc(''); setPdfSrc(''); setExcelSheets([]);
    setActiveSheet(0);

    conveAxios.get(`/rooms/${roomId}/files/${file.id}/content`)
      .then(res => {
        const data: FileContentResponse = res.data.data;
        setContentType(data.type);

        if (data.type === 'image') {
          setImageSrc(`data:${data.mimeType};base64,${data.data}`);

        } else if (data.type === 'pdf') {
          const byteChars = atob(data.data);
          const byteArr = new Uint8Array(byteChars.length);
          for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
          const blob = new Blob([byteArr], { type: 'application/pdf' });
          setPdfSrc(URL.createObjectURL(blob));

        } else if (data.type === 'excel') {
          const binaryStr = atob(data.data);
          const bytes = new Uint8Array(binaryStr.length);
          for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
          const wb = XLSX.read(bytes, { type: 'array' });
          const sheets: ExcelSheet[] = wb.SheetNames.map((name: string) => {
            const ws = wb.Sheets[name];
            const rawData: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
            const colCount = rawData.reduce((max: number, row: string[]) => Math.max(max, row.length), 0);
            const data = rawData.map((row: string[]) => {
              const padded = [...row];
              while (padded.length < colCount) padded.push('');
              return padded;
            });
            return { name, data, colCount };
          });
          setExcelSheets(sheets);

        } else {
          setFileContent(data.data || '');
        }
      })
      .catch(() => setFileContent('파일을 불러올 수 없습니다.'))
      .finally(() => setLoading(false));

    return () => {
      setPdfSrc(prev => {
        if (prev.startsWith('blob:')) URL.revokeObjectURL(prev);
        return '';
      });
    };
  }, [item]);

  useEffect(() => {
    if (!item || !isCode) return;
    const lang = (item as CodePanelItem).language;
    setSaveFilename(`code.${LANG_EXT[lang] || 'txt'}`);
  }, [item]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleImageClick = () => {
    const win = window.open('', '_blank');
    if (win) {
      win.document.write(`
        <html>
          <head><title>${isFile ? (item as ConveFile).originalName : ''}</title></head>
          <body style="margin:0;background:#000;display:flex;align-items:center;justify-content:center;min-height:100vh;">
            <img src="${imageSrc}" style="max-width:100%;max-height:100vh;object-fit:contain;"/>
          </body>
        </html>
      `);
      win.document.close();
    }
  };

  const handlePdfNewWindow = () => window.open(pdfSrc, '_blank');

  const getDisplayContent = () => isCode ? (item as CodePanelItem).code : fileContent;
  const getDisplayLanguage = () => {
    if (isCode) return (item as CodePanelItem).language;
    if (isFile) return getLanguage((item as ConveFile).originalName);
    return 'text';
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(getDisplayContent());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveCode = () => {
    const blob = new Blob([getDisplayContent()], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = saveFilename || 'code.txt'; a.click();
    URL.revokeObjectURL(url);
    setShowSaveInput(false);
  };

  const handleDownloadFile = () => {
    if (!isFile) return;
    const file = item as ConveFile;
    const a = document.createElement('a');
    a.href = `/api/conve/rooms/${roomId}/files/${file.id}/download`;
    a.download = file.originalName;
    a.click();
  };

  const getTitle = () => {
    if (isCode) return `${(item as CodePanelItem).language} 코드`;
    if (isFile) return (item as ConveFile).originalName;
    return '';
  };

  const getSubtitle = () => {
    if (isCode) return `${(item as CodePanelItem).code.split('\n').length}줄`;
    if (isFile) return formatSize((item as ConveFile).fileSize);
    return '';
  };

  const getTitleIcon = () => {
    if (isCode) return '💻';
    if (isFile) return getFileIcon((item as ConveFile).mimeType, (item as ConveFile).originalName);
    return '';
  };

  const isImageContent = !isCode && contentType === 'image';
  const isPdfContent   = !isCode && contentType === 'pdf';
  const isExcelContent = !isCode && contentType === 'excel';
  const isTextContent  = isCode  || contentType === 'text';

  const currentSheet = excelSheets[activeSheet];

  return (
    <>
      {isOpen && <div className="conve-panel-overlay" onClick={handleOverlayClick} />}

      <div
        className={`conve-file-panel ${isOpen ? 'open' : ''}`}
        style={isOpen && width ? { width: `${width}px` } : undefined}
      >
        {item && (
          <>
            <div className="conve-panel-header">
              <div className="conve-panel-file-info">
                <div className="conve-panel-meta">
                  <div className="conve-panel-filename" title={getTitle()}>
                    <span style={{ marginRight: 6 }}>{getTitleIcon()}</span>
                    {getTitle()}
                  </div>
                  <div className="conve-panel-size">{getSubtitle()}</div>
                </div>
              </div>
              <div className="conve-panel-actions">
                {isTextContent && (
                  <button className="conve-panel-btn" onClick={handleCopy}>
                    {copied ? '✓ 복사됨' : '복사'}
                  </button>
                )}
                {isPdfContent && (
                  <button className="conve-panel-btn" onClick={handlePdfNewWindow}>
                    🔍 크게 보기
                  </button>
                )}
                {isCode ? (
                  showSaveInput ? (
                    <div className="conve-save-input-wrap">
                      <input
                        className="conve-save-input"
                        value={saveFilename}
                        onChange={e => setSaveFilename(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') handleSaveCode();
                          if (e.key === 'Escape') setShowSaveInput(false);
                        }}
                        autoFocus
                        placeholder="파일명.확장자"
                      />
                      <button className="conve-panel-btn primary" onClick={handleSaveCode}>저장</button>
                      <button className="conve-panel-btn" onClick={() => setShowSaveInput(false)}>취소</button>
                    </div>
                  ) : (
                    <button className="conve-panel-btn primary" onClick={() => setShowSaveInput(true)}>
                      ↓ 저장
                    </button>
                  )
                ) : (
                  <button className="conve-panel-btn" onClick={handleDownloadFile}>↓ 저장</button>
                )}
                <button className="conve-panel-close" onClick={onClose} title="닫기 (ESC)">✕</button>
              </div>
            </div>

            <div className="conve-panel-body">
              {loading ? (
                <div className="conve-panel-loading">
                  <span className="conve-spinner-sm" />
                  파일 불러오는 중...
                </div>
              ) : isImageContent ? (
                <div className="conve-panel-img-wrap">
                  <img
                    src={imageSrc}
                    alt={isFile ? (item as ConveFile).originalName : ''}
                    className="conve-panel-img"
                    style={{ cursor: 'zoom-in' }}
                    onClick={handleImageClick}
                  />
                </div>
              ) : isPdfContent ? (
                <div className="conve-panel-pdf-wrap">
                  <iframe src={pdfSrc} title="PDF" className="conve-panel-pdf" />
                </div>
              ) : isExcelContent && currentSheet ? (
                <div className="conve-excel-container">
                  <div className="conve-panel-excel-wrap">
                    <table className="conve-excel-table">
                      <thead>
                        <tr>
                          <th className="conve-excel-corner" />
                          {Array.from({ length: currentSheet.colCount }, (_, ci) => (
                            <th key={ci} className="conve-excel-col-header">{colLabel(ci)}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {currentSheet.data.map((row, ri) => (
                          <tr key={ri}>
                            <td className="conve-excel-row-num">{ri + 1}</td>
                            {row.map((cell, ci) => (
                              <td key={ci} className="conve-excel-td">{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="conve-excel-tabs">
                    {excelSheets.map((sheet, i) => (
                      <button
                        key={i}
                        className={`conve-excel-tab ${i === activeSheet ? 'active' : ''}`}
                        onClick={() => setActiveSheet(i)}
                      >
                        {sheet.name}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="conve-panel-code">
                  <SyntaxHighlighter
                    style={theme === 'light' ? oneLight : oneDark}
                    language={getDisplayLanguage()}
                    showLineNumbers
                    wrapLines
                    lineNumberStyle={{ color: 'var(--panel-line-num-color)', minWidth: '2.5em' }}
                    lineNumberContainerStyle={{ background: 'var(--panel-bg)' }}
                    customStyle={{
                      margin: 0,
                      borderRadius: 0,
                      background: 'transparent',
                      fontSize: '13.5px',
                      height: '100%',
                    }}
                  >
                    {getDisplayContent()}
                  </SyntaxHighlighter>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ConveFilePanel;